import { RoundResultError } from "../round/round-errors.js";
import { selectPlayerHole } from "../round/round-selectors.js";
import type { RoundSnapshotV3 } from "../round/round-types.js";
import type { ContestSnapshot, ContestResultV3 } from "./contest-types.js";
import type { ContestProgress } from "./contest-result.js";
import { contestHoleBonuses } from "./hole-bonus.js";

export function matchProgress(
  snapshot: RoundSnapshotV3,
  contest: ContestSnapshot,
): ContestProgress {
  const wins: [number, number] = [0, 0];
  const bonuses = contestHoleBonuses(snapshot, contest);
  let played = 0;
  let complete = false;
  for (const number of contest.holeNumbers) {
    const outcome = resolveMatchHole(snapshot, contest, number);
    if (outcome === "pending") break;
    if (outcome !== "tied") wins[outcome] += 1;
    for (const bonus of bonuses.filter((b) => b.holeNumber === number)) {
      const side = contest.sides.findIndex(
        (s) => bonus.winnerId && s.participantIds.includes(bonus.winnerId),
      );
      if (side === 0 || side === 1) wins[side] += 1;
    }
    played += 1;
    const remainingBonusPoints = bonuses.filter(
      (b) => contest.holeNumbers.indexOf(b.holeNumber) >= played,
    ).length;
    if (
      Math.abs(wins[0] - wins[1]) >
        contest.holeNumbers.length - played + remainingBonusPoints ||
      played === contest.holeNumbers.length
    ) {
      complete = true;
      break;
    }
  }
  if (contest.matchConcession) complete = true;
  const result = matchResult(contest, wins, played, complete);
  if (bonuses.length) result.unit = "pts";
  return { complete, result };
}

export function resolveMatchHole(
  snapshot: RoundSnapshotV3,
  contest: ContestSnapshot,
  number: number,
): 0 | 1 | "tied" | "pending" {
  const conceded = contest.holeConcessions?.[number];
  if (conceded)
    return conceded.concedingSideId === contest.sides[0]!.id ? 1 : 0;
  const values = contest.sides.map((side) =>
    selectPlayerHole(snapshot, side.participantIds[0]!, number),
  );
  if (
    values.some(
      ({ entry }) => entry.status !== "scored" || entry.strokes === null,
    )
  )
    return "pending";
  if (
    values.some(({ contestReceivedStrokes }) => contestReceivedStrokes === null)
  )
    throw new RoundResultError("The agreed match handicap basis is missing.");
  // Remainders inherit the original allocation on these physical holes, not a newly scaled PH.
  const left = values[0]!.entry.strokes! - values[0]!.contestReceivedStrokes!;
  const right = values[1]!.entry.strokes! - values[1]!.contestReceivedStrokes!;
  return left === right ? "tied" : left < right ? 0 : 1;
}

function matchResult(
  contest: ContestSnapshot,
  wins: [number, number],
  played: number,
  complete: boolean,
): ContestResultV3 {
  const concession = contest.matchConcession;
  const margin = Math.abs(wins[0] - wins[1]);
  const remainingHoles = contest.holeNumbers.length - played;
  const leadSideId = concession
    ? contest.sides.find((side) => side.id !== concession.concedingSideId)!.id
    : wins[0] === wins[1]
      ? null
      : contest.sides[wins[0] > wins[1] ? 0 : 1]!.id;
  const winnerSideIds = complete
    ? leadSideId
      ? [leadSideId]
      : contest.sides.map((side) => side.id)
    : [];
  const decisionReason = concession
    ? "match-conceded"
    : !complete
      ? "unfinished"
      : remainingHoles > 0
        ? "early-win"
        : "completed";
  return {
    calculationVersion: "3",
    winnerSideIds,
    tied: complete && leadSideId === null,
    sideTotals: contest.sides.map((side, index) => ({
      sideId: side.id,
      value: wins[index]!,
    })),
    unit: "holes",
    holesPlayed: played,
    decidedAtHole: complete ? (contest.holeNumbers[played - 1] ?? null) : null,
    leadSideId,
    margin,
    remainingHoles,
    dormie: !complete && margin > 0 && margin === remainingHoles,
    decisionReason,
    label: concession
      ? "Conceded"
      : leadSideId === null
        ? "All square"
        : complete && remainingHoles > 0
          ? `${margin}&${remainingHoles}`
          : `${margin} up`,
  };
}
